<template>
  <v-dialog
    :model-value="modelValue"
    class="bq-status-dialog-root"
    content-class="bq-status-dialog-root"
    max-width="540"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card rounded="xl" class="user-dialog-card bq-status-dialog">
      <div class="dialog-header bq-status-dialog__header">
        <div class="bq-status-dialog__header-main">
          <div class="dialog-header__icon bq-status-dialog__icon-wrap" :class="toneClass">
            <v-icon :color="iconColor" size="20">{{ iconName }}</v-icon>
          </div>
          <div class="bq-status-dialog__head-copy">
            <span class="dialog-header__title">{{ titleText }}</span>
            <span class="bq-status-dialog__subtitle">{{ subtitleText }}</span>
          </div>
        </div>
        <v-btn
          icon
          variant="flat"
          size="small"
          class="dialog-close-btn bq-status-dialog__close"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="dialog-body bq-status-dialog__body">
        <p class="bq-status-dialog__lead" v-html="confirmText" />

        <div class="bq-status-dialog__target">
          <p class="bq-status-dialog__name">{{ query?.name || 'Lead' }}</p>
          <p class="bq-status-dialog__meta">
            {{ contactLine }}
          </p>
          <p class="bq-status-dialog__meta">
            Current status: {{ query?.statusLabel || '—' }}
          </p>
        </div>

        <p
          v-if="query?.message && selectedStatus !== 'CONTACTED'"
          class="bq-status-dialog__inquiry"
          :title="query.message"
        >
          {{ query.message }}
        </p>

        <label class="bq-status-dialog__field">
          <span class="bq-status-dialog__label">Comments</span>
          <v-textarea
            v-model="comments"
            placeholder="Add a note for this status change"
            variant="solo"
            density="comfortable"
            rows="3"
            auto-grow
            hide-details="auto"
            flat
            :disabled="saving"
            class="bq-status-dialog__input"
          />
        </label>
      </v-card-text>

      <v-card-actions class="dialog-footer bq-status-dialog__footer">
        <v-spacer />
        <v-btn
          variant="flat"
          class="text-none dialog-btn bq-status-dialog__cancel"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          variant="flat"
          class="text-none dialog-btn bq-status-dialog__confirm"
          :class="confirmButtonClass"
          rounded="lg"
          :loading="saving"
          :disabled="!canSave"
          @click="confirm"
        >
          {{ actionLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { businessQueryStatusLabel } from '@/utils/businessQueries'
import '@/styles/business-queries/status-dialog.css'

defineOptions({ name: 'UpdateBusinessQueryStatusDialog' })

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  query: { type: Object, default: null },
  targetStatus: { type: String, default: '' },
  saving: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'confirm'])

const selectedStatus = ref('')
const comments = ref('')

const STATUS_META = {
  CONTACTED: {
    title: 'Mark as contacted',
    subtitle: 'This will update the submission workflow status.',
    actionLabel: 'Contacted',
    actionColor: 'primary',
    icon: 'mdi-phone-check-outline',
    iconColor: '#1d4ed8',
    toneClass: 'bq-status-dialog__icon-wrap--contacted',
  },
  CONVERTED: {
    title: 'Mark as converted',
    subtitle: 'This will update the submission workflow status.',
    actionLabel: 'Converted',
    actionColor: 'primary',
    icon: 'mdi-check-circle-outline',
    iconColor: '#059669',
    toneClass: 'bq-status-dialog__icon-wrap--converted',
  },
  PENDING: {
    title: 'Mark as pending',
    subtitle: 'This will update the submission workflow status.',
    actionLabel: 'Pending',
    actionColor: 'secondary',
    icon: 'mdi-clock-outline',
    iconColor: '#64748b',
    toneClass: 'bq-status-dialog__icon-wrap--pending',
  },
  REJECTED: {
    title: 'Reject inquiry',
    subtitle: 'This will update the submission workflow status.',
    actionLabel: 'Rejected',
    actionColor: 'error',
    icon: 'mdi-close-circle-outline',
    iconColor: '#dc2626',
    toneClass: 'bq-status-dialog__icon-wrap--rejected',
  },
}

const currentMeta = computed(() => {
  const key = String(selectedStatus.value || '').trim().toUpperCase()
  if (STATUS_META[key]) return STATUS_META[key]

  // Custom context-defined statuses get generic dynamic meta.
  const label = businessQueryStatusLabel(key)
  return {
    title: key ? `Mark as ${label}` : 'Update query status',
    subtitle: 'This will update the submission workflow status.',
    actionLabel: key ? label : 'Save',
    actionColor: 'primary',
    icon: 'mdi-flag-outline',
    iconColor: '#1d4ed8',
    toneClass: 'bq-status-dialog__icon-wrap--contacted',
  }
})

const canSave = computed(() => {
  const next = String(selectedStatus.value || '').trim().toUpperCase()
  const current = String(props.query?.status || '').trim().toUpperCase()
  const hasComments = Boolean(String(comments.value || '').trim())
  return (
    Boolean(next) &&
    next !== current &&
    Boolean(props.query?.requestId) &&
    hasComments
  )
})

watch(
  () => [props.modelValue, props.query?.status, props.targetStatus],
  ([open]) => {
    if (open) {
      selectedStatus.value = String(props.targetStatus || props.query?.status || 'PENDING').toUpperCase()
      comments.value = ''
    }
  },
)

function close() {
  if (props.saving) return
  emit('update:modelValue', false)
}

function confirm() {
  if (!canSave.value) return
  emit('confirm', {
    requestId: props.query.requestId,
    requestStatus: selectedStatus.value,
    comments: String(comments.value || '').trim(),
  })
}

const titleText = computed(() => currentMeta.value.title)
const subtitleText = computed(() => currentMeta.value.subtitle)
const actionLabel = computed(() => currentMeta.value.actionLabel)
const iconName = computed(() => currentMeta.value.icon)
const iconColor = computed(() => currentMeta.value.iconColor)
const toneClass = computed(() => currentMeta.value.toneClass)

const confirmButtonClass = computed(() => {
  const status = String(selectedStatus.value || '').trim().toUpperCase()
  if (status === 'REJECTED') return 'bq-status-dialog__confirm--error'
  if (status === 'PENDING') return 'bq-status-dialog__confirm--neutral'
  return 'bq-status-dialog__confirm--primary'
})

const confirmText = computed(() => {
  if (selectedStatus.value === 'CONVERTED') {
    return 'Are you sure you want to mark this inquiry as <strong>Converted</strong>?'
  }
  if (selectedStatus.value === 'REJECTED') {
    return 'Are you sure you want to mark this inquiry as <strong>Rejected</strong>?'
  }
  if (selectedStatus.value === 'CONTACTED') {
    return 'Are you sure you want to mark this inquiry as <strong>Contacted</strong>?'
  }
  if (selectedStatus.value === 'PENDING') {
    return 'Are you sure you want to mark this inquiry as <strong>Pending</strong>?'
  }
  const key = String(selectedStatus.value || '').trim().toUpperCase()
  if (key) {
    return `Are you sure you want to mark this inquiry as <strong>${businessQueryStatusLabel(key)}</strong>?`
  }
  return 'Are you sure you want to update this inquiry status?'
})

const contactLine = computed(() => {
  const email = props.query?.email && props.query.email !== '—' ? props.query.email : ''
  const phone = props.query?.phone || ''
  return [email, phone].filter(Boolean).join(' · ') || '—'
})
</script>
